// src/components/workspace/resolution/PreResolutionModal.js

import { AlertTriangle, Check, X } from "lucide-react";

/**
 * The gate between "we think it's fixed" and closing the incident.
 *
 * Two different things are being checked here and they must not be confused.
 * Blockers are hard stops enforced elsewhere too (the resolution report is
 * required before an expert can close). Gaps are quality warnings — the
 * incident can still close, but the record will be thin, and a thin record is
 * worthless to the resolution intelligence database later.
 *
 * Showing both in one place is deliberate: the previous flow used a generic
 * confirm() that gave no indication of what was actually missing.
 */

function Row({ ok, children }) {
  return (
    <li className="flex items-start gap-2.5">
      {ok ? (
        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
      ) : (
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
      )}
      <span className={`text-sm ${ok ? "text-slate-500" : "text-slate-800"}`}>
        {children}
      </span>
    </li>
  );
}

export default function PreResolutionModal({
  open,
  viewerRole,
  checks,
  onClose,
  onProceed,
}) {
  if (!open) return null;

  const blockers = checks.filter((c) => !c.ok && c.blocking);
  const gaps = checks.filter((c) => !c.ok && !c.blocking);
  const canProceed = blockers.length === 0;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-slate-900 bg-opacity-60 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Before closing this incident
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {canProceed
                ? "Review what the record will contain."
                : "Something still needs doing first."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <ul className="space-y-2">
            {checks.map((check) => (
              <Row key={check.id} ok={check.ok}>
                {check.label}
              </Row>
            ))}
          </ul>

          {gaps.length > 0 && canProceed && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              You can still close this incident, but the resolution record will
              be incomplete
              {viewerRole === "expert"
                ? " — and it is what the customer receives."
                : "."}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-white"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={onProceed}
            disabled={!canProceed}
            className="rounded-lg bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Mark resolved
          </button>
        </div>
      </div>
    </div>
  );
}
